/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../../contexts/onboarding-context';
import { useAuth } from '../../../contexts/auth/auth-context';
import { onboardingApi } from '../../../api/onboarding/onboarding';
import { OnboardingContainer, AnswerSubmitted, AnswerSubmittedOverlay, COLORS } from '../styles';
import { QuestionContent } from './components/question-content';
import { QuestionHeader } from './components/question-header';
import { Main } from './styles/layout';

import { Header } from '../../chat/header/header';
import { AnimatedDockContainer } from '../dock-animations/AnimatedDockContainer';
import { slideUpAnimation, slideDownAnimation } from '../animations';
import { getUserFriendlyErrorMessage } from '../../../utils/error-messages';
import { ensureValidToken } from '../../../utils/ensure-valid-token';
import { saveDraft, deleteDraft } from '../../../utils/draft-storage';
import { useDraftRecovery } from '../../../hooks/use-draft-recovery';

type OverlayPhase = 'hidden' | 'visible' | 'fading';

type LastSubmission =
  | { type: 'text'; conversationId: string; text: string; files?: File[] | undefined }
  | {
      type: 'record';
      conversationId: string;
      audioBlob: Blob;
      files?: File[] | undefined;
      transcript?: string | undefined;
      audioFile?: File | undefined;
      duration?: number | undefined;
    };

/* ── Styled components ─────────────────────────────────────────────── */

const SendingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(26, 26, 28, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9998;
  gap: 20px;
`;

const SendingLabel = styled.span`
  color: ${COLORS.textSecondary};
  font-size: 15px;
  font-family: Inter, sans-serif;
  font-weight: 500;
  line-height: 20px;
`;

const SendingProgressTrack = styled.div`
  width: 200px;
  height: 4px;
  background: ${COLORS.cardElevated};
  border-radius: 9999px;
  overflow: hidden;
`;

const SendingProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: ${COLORS.accent};
  border-radius: 9999px;
  width: ${(props) => props.progress}%;
  transition: width 0.3s ease;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin: 0 16px 8px;
  background: rgba(214, 95, 70, 0.12);
  border: 1px solid rgba(214, 95, 70, 0.3);
  border-radius: 12px;
  color: ${COLORS.textPrimary};
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 150%;
`;

const ErrorText = styled.span`
  flex: 1;
`;

const RetryButton = styled.button`
  color: ${COLORS.accent};
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  white-space: nowrap;
`;

/* ── Component ────────────────────────────────────────────────────── */

export const OnboardingQuestion: React.FC = () => {
  const [overlayPhase, setOverlayPhase] = useState<OverlayPhase>('hidden');
  const [dockResetTrigger, setDockResetTrigger] = useState(0);
  const [localIndex, setLocalIndex] = useState<number | null>(null);
  const [displayedIndex, setDisplayedIndex] = useState<number | null>(null);
  const [isExiting, setIsExiting] = useState(false);
  const [isTypewriterActive, setIsTypewriterActive] = useState(true);
  const [contentKey, setContentKey] = useState(0);

  // Submission hardening states
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const lastSubmissionRef = useRef<LastSubmission | null>(null);

  const navigate = useNavigate();
  const { syncAuthFromStorage } = useAuth();

  const { questions, currentQuestionIndex, submitAnswer, totalQuestions, markOnboardingCompleted } =
    useOnboarding();

  // Draft recovery on mount
  useDraftRecovery(submitAnswer);

  // Use a ref so timeouts always see latest questions without stale closures
  const questionsRef = useRef(questions);
  useEffect(() => { questionsRef.current = questions; }, [questions]);

  const activeIndex = localIndex !== null ? localIndex : currentQuestionIndex;
  const displayIndex = displayedIndex !== null ? displayedIndex : currentQuestionIndex;
  const currentQuestion = questions[activeIndex];
  const displayedQuestion = questions[displayIndex];

  // The last question in the sequence shows only "Done" (no Type/Record)
  const isLastQuestion = displayIndex === questions.length - 1;

  // Content is visible only when overlay is hidden AND not in the exit blank phase
  const showContent = overlayPhase === 'hidden' && !isExiting;

  const handleTypeClick = () => {};
  const handleRecordClick = () => {};

  const findNextUnansweredIndex = (afterIndex: number) => {
    const qs = questionsRef.current;
    for (let i = afterIndex + 1; i < qs.length; i++) {
      if (!qs[i].is_answered) return i;
    }
    return -1;
  };

  const runTransition = (nextLocalIndex: number | null, nextDisplayIndex: number) => {
    // Phase 1: show overlay — content stays rendered but covered
    setOverlayPhase('visible');

    // Phase 2: after 2s fade overlay out
    setTimeout(() => {
      setOverlayPhase('fading');

      // Phase 3: fade done → hide overlay, blank the screen briefly
      setTimeout(() => {
        setOverlayPhase('hidden');
        setIsExiting(true);

        // Phase 4: swap content while screen is blank, then cascade in
        setTimeout(() => {
          setIsTypewriterActive(false);
          if (nextLocalIndex !== null) setLocalIndex(nextLocalIndex);
          setDisplayedIndex(nextDisplayIndex);
          setDockResetTrigger((prev) => prev + 1);
          setContentKey((prev) => prev + 1);
          setIsExiting(false);
          // Small delay then start typewriter so enter animations play first
          setTimeout(() => setIsTypewriterActive(true), 300);
        }, 80);
      }, 300);
    }, 2000);
  };

  const handleTypeSubmit = async (text: string, files?: File[]) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmissionError(null);
    setUploadProgress(5); // Show progress bar immediately so user sees activity
    setIsTypewriterActive(false);

    const nextIdx = findNextUnansweredIndex(activeIndex);

    // Store for retry
    lastSubmissionRef.current = {
      type: 'text',
      conversationId: currentQuestion.conversation_id,
      text,
      files,
    };

    try {
      // Save draft before submission (insurance against crashes/network loss)
      await saveDraft({
        conversationId: currentQuestion.conversation_id,
        content: text,
        timestamp: Date.now(),
      });

      // Ensure token is valid before submission — if refresh fails,
      // bail out gracefully instead of triggering a 401 cascade → splash.
      const tokenValid = await ensureValidToken();
      if (!tokenValid) {
        setIsTypewriterActive(true);
        setSubmissionError(
          'Your session expired. Please sign in again — your answer is saved and will be sent automatically.',
        );
        return;
      }

      await submitAnswer(
        currentQuestion.conversation_id,
        text,
        files,
        undefined,
        undefined,
        (progress) => setUploadProgress(progress),
      );

      // Success: clear draft and transition
      await deleteDraft(currentQuestion.conversation_id);
      setUploadProgress(null);
      runTransition(
        nextIdx !== -1 ? nextIdx : null,
        nextIdx !== -1 ? nextIdx : activeIndex,
      );
    } catch (e: any) {
      console.error('Submission failed:', e);
      setIsTypewriterActive(true);
      setUploadProgress(null);
      setSubmissionError(getUserFriendlyErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecordSend = async (audioBlob: Blob, files?: File[], transcript?: string) => {
    if (submitting) return;
    setSubmitting(true);
    setSubmissionError(null);
    setUploadProgress(5); // Show progress bar immediately so user sees activity
    setIsTypewriterActive(false);

    let duration: number | undefined;
    try {
      const AudioContextClass = (window.AudioContext || (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext) as typeof AudioContext;
      const audioContext = new AudioContextClass();
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      duration = Math.round(audioBuffer.duration);
      audioContext.close();
    } catch (error) {
      console.error('Error calculating audio duration:', error);
    }

    // Only attach audio if it has real content
    const hasRealAudio = duration !== undefined && duration > 0;
    const audioFile = hasRealAudio
      ? new File([audioBlob], `recording-${Date.now()}.webm`, { type: audioBlob.type })
      : undefined;

    const questionToAnswer = currentQuestion;
    const nextIdx = findNextUnansweredIndex(activeIndex);

    // Store for retry
    lastSubmissionRef.current = {
      type: 'record',
      conversationId: questionToAnswer.conversation_id,
      audioBlob,
      files,
      transcript,
      audioFile,
      duration: hasRealAudio ? duration : undefined,
    };

    try {
      // Save draft (including audio blob) before submission
      await saveDraft({
        conversationId: questionToAnswer.conversation_id,
        content: transcript || '',
        audioBlob: hasRealAudio ? audioBlob : undefined,
        audioDuration: hasRealAudio ? duration : undefined,
        timestamp: Date.now(),
      });

      // Ensure token is valid before submission — if refresh fails,
      // bail out gracefully instead of triggering a 401 cascade → splash.
      const tokenValid = await ensureValidToken();
      if (!tokenValid) {
        setIsTypewriterActive(true);
        setSubmissionError(
          'Your session expired. Please sign in again — your recording is saved and will be sent automatically.',
        );
        return;
      }

      // If audio is present but no transcript, tell backend to transcribe silently
      const needsBackendTranscription = hasRealAudio && (!transcript || !transcript.trim());

      await submitAnswer(
        questionToAnswer.conversation_id,
        transcript || '',
        files,
        audioFile,
        hasRealAudio ? duration : undefined,
        (progress) => setUploadProgress(progress),
        needsBackendTranscription,
      );

      // Success: clear draft and transition
      await deleteDraft(questionToAnswer.conversation_id);
      setUploadProgress(null);
      runTransition(
        nextIdx !== -1 ? nextIdx : null,
        nextIdx !== -1 ? nextIdx : activeIndex,
      );
    } catch (e: any) {
      console.error('Submission failed:', e);
      setLocalIndex(null);
      setIsTypewriterActive(true);
      setUploadProgress(null);
      setSubmissionError(getUserFriendlyErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetry = useCallback(async () => {
    const last = lastSubmissionRef.current;
    if (!last) return;

    if (last.type === 'text') {
      await handleTypeSubmit(last.text, last.files);
    } else {
      await handleRecordSend(last.audioBlob, last.files, last.transcript);
    }
  }, []);

  const handleDoneClick = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setSubmissionError(null);

    try {
      const tokenValid = await ensureValidToken();
      if (!tokenValid) {
        setSubmissionError('Your session expired. Please sign in again.');
        return;
      }

      // Mark onboarding complete without creating an empty answer message
      await onboardingApi.complete();

      // Set isCompleted in onboarding context + update profile
      await markOnboardingCompleted();

      // Sync profile to get updated onboarding_completed status
      await syncAuthFromStorage();
      navigate('/chat');
    } catch (e: any) {
      console.error('Done click failed:', e);
      setSubmissionError(getUserFriendlyErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }, [submitting, syncAuthFromStorage, navigate]);

  // Mark question as seen when displayed (fire-and-forget)
  useEffect(() => {
    if (displayedQuestion?.conversation_id) {
      onboardingApi.markSeen(displayedQuestion.conversation_id).catch(() => {});
    }
  }, [displayedQuestion?.conversation_id]);

  useEffect(() => {
    if (questions.length > 0 && displayedIndex === null) {
      setDisplayedIndex(currentQuestionIndex);
    }
  }, [questions.length, currentQuestionIndex, displayedIndex]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.classList.remove('allow-scroll');
    return () => {
      document.body.style.overflow = '';
      document.body.classList.add('allow-scroll');
    };
  }, []);

  if (!currentQuestion) {
    return (
      <OnboardingContainer>
        <Header
          onNotificationClick={() => console.log('Notification clicked')}
          onProfileClick={() => console.log('Profile clicked')}
        />
        <Main>
          <div>No questions available</div>
        </Main>
      </OnboardingContainer>
    );
  }

  return (
    <OnboardingContainer>
      <div css={slideDownAnimation(0.45, 0)}>
        <Header
          onNotificationClick={() => console.log('Notification clicked')}
          onProfileClick={() => console.log('Profile clicked')}
        />
      </div>
      <Main
        style={{
          paddingBottom: '0px',
          paddingTop: '12px',
          overflowY: 'auto',
          overflowX: 'hidden',
          flex: '1 1 0',
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {showContent && (
          <div
            key={contentKey}
            style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <QuestionHeader
              currentQuestionIndex={displayIndex}
              totalQuestions={totalQuestions}
              pendingQuestionsCount={questions.length}
              isLastQuestion={isLastQuestion}
            />
            <QuestionContent
              questionText={displayedQuestion.content}
              audioId={displayedQuestion.audio_id}
              typewriterActive={isTypewriterActive}
            />
          </div>
        )}
      </Main>
      {submitting && (
        <SendingOverlay>
          <SendingLabel>{isLastQuestion ? 'Finalizing…' : 'Sending your answer…'}</SendingLabel>
          <SendingProgressTrack>
            <SendingProgressFill progress={uploadProgress ?? 5} />
          </SendingProgressTrack>
        </SendingOverlay>
      )}
      {overlayPhase !== 'hidden' && (
        <AnswerSubmittedOverlay
          style={{
            opacity: overlayPhase === 'fading' ? 0 : 1,
            transition: overlayPhase === 'fading' ? 'opacity 0.3s ease' : 'none',
          }}
        >
          <AnswerSubmitted>Your answer was submitted</AnswerSubmitted>
        </AnswerSubmittedOverlay>
      )}
      {submissionError && showContent && (
        <ErrorBanner>
          <ErrorText>{submissionError}</ErrorText>
          <RetryButton onClick={handleRetry}>Retry</RetryButton>
        </ErrorBanner>
      )}
      {showContent && (
        <div css={slideUpAnimation(0.5, 0.4)}>
          <AnimatedDockContainer
            defaultVariant={isLastQuestion ? 'done' : 'question'}
            onTypeClick={handleTypeClick}
            onRecordClick={handleRecordClick}
            onTypeSubmit={handleTypeSubmit}
            typeInitialText=""
            onRecordSend={handleRecordSend}
            onDoneClick={handleDoneClick}
            isCompleted={isLastQuestion}
            resetToDefault={dockResetTrigger > 0}
            currentQuestionConversationId={currentQuestion.conversation_id}
            isSubmitting={submitting}
            uploadProgress={uploadProgress}
          />
        </div>
      )}
    </OnboardingContainer>
  );
};
